// main.tsx -- Full boot sequence per DATA-LAYER.md + D-09.
// Ordering: handleAuthRedirect -> enableMocking -> createRoot (Pitfall 3).

import './styles/v2.css';
import './styles/app.css';
import './styles/overhaul.css';
import './styles/ask.css'; // /ask peer-surface shell (token CSS); disjoint selectors from tailwind below
import './styles/redesign.css'; // redesign tokens (--rd-*) + .rd scoped preflight stand-in — before tailwind so utilities win ties
import './styles/tailwind.css'; // last: unlayered utilities win source-order ties over the above

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { handleAuthRedirect } from './lib/auth-redirect';
import { configureApi } from './lib/api';
import { initAnalytics } from './lib/analytics';
import { App } from './App';
import { ErrorBoundary } from './components/shared/ErrorBoundary';

// D-07: Configure API base URL from env. Empty string in dev (Vite proxy).
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
configureApi({ baseUrl: apiBaseUrl });

// D-47: Default staleTime and gcTime; per-hook overrides take precedence.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,   // 2 min default
      gcTime: 10 * 60 * 1000,     // 10 min cache retention
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

async function enableMocking() {
  if (import.meta.env.DEV && !(window as any).__SKIP_MSW__) {
    const { worker } = await import('./mocks/browser');
    return worker.start({ onUnhandledRequest: 'bypass' });
  }
}

async function boot() {
  // The terum-capture CLI handshake lives at a real path, outside the HashRouter.
  // Render it directly and skip the normal app boot (auth gate, MSW, surfaces).
  if (window.location.pathname === '/auth/mcp-setup') {
    const { renderMcpSetup } = await import('./components/McpSetup');
    renderMcpSetup();
    return;
  }

  await handleAuthRedirect();
  await enableMocking();
  // PostHog: no-ops entirely without VITE_POSTHOG_KEY, so dev/MSW stay silent. Skipped on the
  // /auth/mcp-setup branch above (early return) since that CLI handshake has no analytics value.
  initAnalytics();
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorBoundary label="Terum">
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}

boot();
